/**
 * A published analysis from the older contract, shown as what it is.
 *
 * WHY THIS EXISTS. A 1.0.0 report has no per-node claim keys, no identity
 * provenance and no grounded-statement labels — those arrived with 1.1.0. The
 * current components are built around them: every material node carries a
 * `claimKey` and offers "View sources" from it. Handing an old body to them
 * would require inventing twelve kinds of provenance, so the screen refused to
 * draw one at all — and a founder with five published analyses saw nothing.
 *
 * Refusing was half right. The provenance must not be invented; the FINDINGS
 * were still published, and hiding them serves nobody. So this reader shows the
 * substance and offers no citation affordance anywhere — not a disabled one, not
 * an empty one. There is nothing to cite with, and the absence is stated once,
 * at the top, rather than implied by a dead control on every row.
 *
 * It is deliberately plainer than the current bodies. This is a record of what
 * was assessed, not a document to work from; the way to get a citable analysis
 * is to refresh, which the card above this one offers.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import type { LegacyReport } from '@/lib/strategic-analysis/report-contract';

/** A heading and a list of lines. Every shape below reduces to this. */
interface Section {
  heading: string;
  lines: string[];
}

const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * The findings, flattened.
 *
 * Exported so the reduction is assertable without rendering — the risk in a
 * legacy reader is showing the WRONG rows, not styling them badly.
 */
export function legacySections(report: LegacyReport): Section[] {
  switch (report.analysisType) {
    case 'competition': {
      const { body } = report;
      const sections: Section[] = [{
        heading: 'Alternatives assessed',
        lines: body.entities.map((e) => `${e.name} — ${titleCase(e.classification)}`),
      }];
      if (body.axes.length > 0) {
        sections.push({
          heading: 'Axes compared',
          lines: body.axes.map((a) => `${titleCase(a.axis)}: ${a.lowLabel} → ${a.highLabel}`),
        });
      }
      return sections;
    }
    case 'porter': {
      const { body } = report;
      return [
        {
          heading: 'Forces',
          lines: report.body.forces.map((f) => `${titleCase(f.force)} — ${titleCase(f.rating)}`),
        },
        ...(body.overallSynthesis
          ? [{ heading: 'Synthesis', lines: [body.overallSynthesis] }]
          : []),
      ];
    }
    case 'pestel': {
      const { body } = report;
      return body.categories.map((c) => ({
        heading: `${titleCase(c.category)} — ${titleCase(c.posture)}`,
        lines: c.items.map((i) => i.summary),
      }));
    }
    case 'market_validation': {
      const { body } = report;
      return [{
        heading: 'Conclusion',
        lines: [titleCase(body.conclusion), body.conclusionExplanation].filter(Boolean),
      }];
    }
    case 'exit_strategy': {
      const { body } = report;
      return [{
        heading: 'Pathways',
        lines: body.pathways.map((p) => `${titleCase(p.pathway)} — ${titleCase(p.tier)}`),
      }];
    }
  }
}

export function LegacyAnalysisBody({ report }: { report: LegacyReport }) {
  const colors = useThemeColors();
  const sections = legacySections(report).filter((s) => s.lines.length > 0);

  if (sections.length === 0) return null;

  return (
    <View
      className="rounded-2xl p-4 gap-4"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
        overflow: 'hidden',
      }}
    >
      {/* SAID ONCE, AT THE TOP. Repeating "no sources" beside every row would
          make the absence louder than the findings, and a disabled "View
          sources" control on each would imply a citation exists to reveal. */}
      <Text className="text-[12px] leading-4" style={{ color: colors.text.secondary }}>
        What this analysis found, as it was published. Sources are not shown because this
        report predates the evidence contract — refresh it to get one with every finding linked
        to its source.
      </Text>

      {sections.map((section, si) => (
        <View key={`legacy-section-${si}`} className="gap-1.5">
          <Text
            numberOfLines={2}
            className="text-[13px] font-semibold"
            style={{ color: colors.text.primary }}
          >
            {section.heading}
          </Text>
          {section.lines.map((line, li) => (
            <View key={`legacy-line-${si}-${li}`} className="flex-row gap-2">
              <Text className="text-[13px] shrink-0" style={{ color: colors.text.tertiary }}>
                •
              </Text>
              <Text className="flex-1 text-[13px] leading-5" style={{ color: colors.text.secondary }}>
                {line}
              </Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}
